import { useMemo } from 'react'
import { useStoreState } from 'easy-peasy'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/queryKeys'
import { api } from '../../services/api'

function useGetFirstLeague() {
  return useQuery({
    queryKey: queryKeys.allMatches('all', '1'),
    queryFn: async () => {
      const response = await api.fetchMatches(`1-bundesliga`)
      return response.playing_schedules
    },
    enabled: true,
    staleTime: 2 * 60 * 1000, 
  })
}

function useGetSecondLeagueNO() {
  return useQuery({
      queryKey: queryKeys.matchesByRegion('NO', '2'),
      queryFn: async () => {
        const response = await api.fetchMatches(`2-bundesliga-nord-ost`)
        return response.playing_schedules
      },
      enabled: true,
      staleTime: 2 * 60 * 1000, 
    })
  } 
function useGetSecondLeagueSW() {
  return useQuery({
    queryKey: queryKeys.matchesByRegion('SW', '2'),
    queryFn: async () => {
      const response = await api.fetchMatches(`2-bundesliga-sued-west`)
      return response.playing_schedules
    },
    enabled: true,
    staleTime: 2 * 60 * 1000,
  })
}

const useMatchesFirstLeague = () => {
  const query = useGetFirstLeague()

  // Helper function for date parsing 
  const parsePastDate = (dateStr) => {
    const [day, month, year] = dateStr.split('.')
    return new Date(`${year}-${month}-${day}`)
  }

  // Combined loading state
  const isLoading = query.isLoading 
  // Combined error state
  const error = query.error

  //sorted data
  const data = useMemo(() => {
    if (!query.data) return []
    
    const allMatches = [...query.data]
    return allMatches.sort((a, b) => parsePastDate(a.date) - parsePastDate(b.date))
  }, [query.data])

  return {
    data,
    isLoading,
    error,
    
    firstLeagueMatches: query.data || [],
    isErrorFirstLeague: query.isError,
  }

}
const useMatchesSecondLeague = () => {
  const noQuery = useGetSecondLeagueNO()
  const swQuery = useGetSecondLeagueSW()

  // Helper function for date parsing 
  const parsePastDate = (dateStr) => {
    const [day, month, year] = dateStr.split('.')
    return new Date(`${year}-${month}-${day}`)
  }

  // Combined loading state
  const isLoading = noQuery.isLoading || swQuery.isLoading
  
  // Combined error state
  const error = noQuery.error || swQuery.error

  // Combined and sorted data
  const data = useMemo(() => {
    if (!noQuery.data || !swQuery.data) return []
    
    const allMatches = [...noQuery.data, ...swQuery.data]
    return allMatches.sort((a, b) => parsePastDate(a.date) - parsePastDate(b.date))
  }, [noQuery.data, swQuery.data])

  return {
    data,
    isLoading,
    error,
    
    noMatches: noQuery.data || [],
    swMatches: swQuery.data || [],
    isErrorNO: noQuery.isError,
    isErrorSW: swQuery.isError,
  }
}

export const useLeagueMatches = () => {
  const  selectedLeagueLevel  = useStoreState((state) => state.leagueSelection.selectedLeagueLevel)

  const firstLeagueData = useMatchesFirstLeague()
  const secondLeagueData = useMatchesSecondLeague()

  switch (selectedLeagueLevel) {
    case '1':
      return firstLeagueData
    case '2':
      return secondLeagueData
    default:
      return { data: [], isLoading: false, error: null }
  }
}

export const useAllMatches = () => {
  const firstLeagueData = useGetFirstLeague()
  const secondLeagueNOData = useGetSecondLeagueNO()
  const secondLeagueSWData = useGetSecondLeagueSW()

  
  // combine all matches
  const data = useMemo(() => {
    if (!firstLeagueData.data || !secondLeagueNOData.data || !secondLeagueSWData.data) return []
    
    const allMatches = [...firstLeagueData.data, ...secondLeagueNOData.data, ...secondLeagueSWData.data]
    
    // Helper function for date parsing 
    const parsePastDate = (dateStr) => {
      const [day, month, year] = dateStr.split('.')
      return new Date(`${year}-${month}-${day}`)
    }
    
    return allMatches.sort((a, b) => parsePastDate(a.date) - parsePastDate(b.date))
  }, [firstLeagueData.data, secondLeagueNOData.data, secondLeagueSWData.data])

  // Combine data based on selected league level
  
  // Combine loading states
  const isLoading = firstLeagueData.isLoading || secondLeagueNOData.isLoading || secondLeagueSWData.isLoading

  // Combine error states
  const error = firstLeagueData.error || secondLeagueNOData.error || secondLeagueSWData.error

  return {
    data,
    isLoading,
    error,
  }
}

export const useSliderMatches = (selectedLeagueLevel) => {
  const numberOfMatches = 40
  const { data: allMatches, isLoading, error } = useLeagueMatches(selectedLeagueLevel)

  const sliderMatches = useMemo(() => {
    const currentDate = new Date();
    if (!allMatches || allMatches.length === 0) return []

    // Date parsing functions 
    const parseDate = (dateStr, timeStr) => {
      const [day, month, year] = dateStr.split('.')
      if (!timeStr) timeStr = '00:00'
      return new Date(`${year}-${month}-${day} ${timeStr}`)
    }

    const parsePastDate = (dateStr) => {
      const [day, month, year] = dateStr.split('.')
      return new Date(`${year}-${month}-${day}`)
    }

    // Sort matches 
    const pastMatches = allMatches
      .filter(match => parsePastDate(match.date) < currentDate)
      .sort((a, b) => parsePastDate(a.date) - parsePastDate(b.date))

    const futureMatches = allMatches
      .filter(match => parseDate(match.date, match.game_time) >= currentDate)
      .sort((a, b) => parseDate(a.date, a.game_time) - parseDate(b.date, b.game_time))

    const count = (matches) => {
      if (matches.length > (numberOfMatches/2)) return numberOfMatches/2
      else return matches.length
    }
    const pastCount = count(pastMatches)
    const futureCount = count(futureMatches)
    const matchList = [...pastMatches.slice(-(numberOfMatches-futureCount)), ...futureMatches.slice(0, numberOfMatches-pastCount)]
    return {matchList, pastCount}
  }, [allMatches])

  return {
    data: sliderMatches.matchList || [],
    startingCardIndex: sliderMatches.pastCount - 1 || 0,
    isLoading,
    error,
  }
}

